import type { CSSProperties } from 'react';

import { COLUMN_SPAN_DEFAULT, clampColumnSpan } from '@/lib/blocks/tree';
import { parseSectionOverlayColor, parseSectionOverlayOpacity } from '@/lib/parse-section-overlay';
import {
  DEFAULT_FOCUS_POINT,
  pickStringUnion,
  readBoolean,
  readImageValue,
  readNumber,
  readString,
  readVideoValue,
} from '@/lib/prop-utils';
import { readSectionBackgroundType, type SectionBackgroundType } from '@/lib/section-background';
import { cn } from '@/lib/utils';
import {
  BLOCK_TYPE,
  type BlockType,
  isParentBlockType,
  isVisualMediaBlockType,
  type PageBlock,
} from '@/types/block';
import type { ImageValue } from '@/types/image';
import type { VideoValue } from '@/types/video';
import {
  getBlockDisplayWidthStyle,
  getColumnSpanMaxWidthClassName,
  shouldForceFullWidthBlock,
} from './block-layout';

const GROUP_DIRECTION_VALUES = ['horizontal', 'vertical'] as const;
const GROUP_GAP_VALUES = ['sm', 'md', 'lg'] as const;
const GROUP_ALIGN_VALUES = ['start', 'center', 'end', 'stretch'] as const;
const GROUP_JUSTIFY_VALUES = ['start', 'center', 'end', 'between'] as const;
const GROUP_SURFACE_VALUES = [
  'plain',
  'bordered',
  'filled',
  'bordered-filled',
  'muted',
  'feature-band',
  'top-divider',
  'bleed-bottom',
  'push-down',
] as const;

export type GroupDirection = (typeof GROUP_DIRECTION_VALUES)[number];
export type GroupGap = (typeof GROUP_GAP_VALUES)[number];
export type GroupAlign = (typeof GROUP_ALIGN_VALUES)[number];
export type GroupJustify = (typeof GROUP_JUSTIFY_VALUES)[number];
export type GroupSurface = (typeof GROUP_SURFACE_VALUES)[number];
export type GroupChildFloat = 'left' | 'right';

export const FLOAT_RESTORE_POSITION_PROP = 'floatRestorePosition';

export function getBlockFloat(block: Pick<PageBlock, 'props'>): GroupChildFloat | null {
  const value = readString(block.props?.float);
  return value === 'left' || value === 'right' ? value : null;
}

export function getBlockFloatRestorePosition(block: Pick<PageBlock, 'props'>): number | null {
  const value = readNumber(block.props?.[FLOAT_RESTORE_POSITION_PROP]);
  return value !== undefined && Number.isInteger(value) && value >= 1 ? value : null;
}

export const GROUP_DIRECTION_DEFAULT: GroupDirection = 'horizontal';
export const GROUP_GAP_DEFAULT: GroupGap = 'md';
export const GROUP_ALIGN_DEFAULT: GroupAlign = 'start';
export const GROUP_JUSTIFY_DEFAULT: GroupJustify = 'start';
export const GROUP_SURFACE_DEFAULT: GroupSurface = 'plain';

/** Default float width when no narrower column span is configured. Keep the static Tailwind class
 * and numeric value together so layout-fit detection matches the rendered width. */
const FLOAT_DEFAULT_MAX_WIDTH_PERCENT = 47;
const FLOAT_DEFAULT_MAX_WIDTH_CLASSNAME = 'md:max-w-[47%]';

export interface GroupLayoutConfig {
  direction: GroupDirection;
  gap: GroupGap;
  align: GroupAlign;
  justify: GroupJustify;
  surface: GroupSurface;
  backgroundColor: string | undefined;
  backgroundType: SectionBackgroundType | undefined;
  backgroundImage: ImageValue | undefined;
  backgroundVideo: VideoValue | undefined;
  backgroundOverlay: number;
  backgroundOverlayColor: string;
  backgroundFixed: boolean;
  backgroundFocusX: number;
  backgroundFocusY: number;
  /** When true, horizontal children use flex-1 to fill the full container width. */
  fillWidth: boolean;
  isFloatLayout: boolean;
  /** Exactly two opposing floats whose configured widths can share one row. */
  hasFittingOpposingFloatPair: boolean;
}

type GroupLayoutSettings = Omit<GroupLayoutConfig, 'isFloatLayout' | 'hasFittingOpposingFloatPair'>;

const GROUP_GAP_CLASSNAMES: Record<GroupGap, string> = {
  sm: 'gap-2',
  md: 'gap-4',
  lg: 'gap-6',
};

/** Margin-based equivalent of GROUP_GAP_CLASSNAMES for float mode, where `gap` doesn't apply
 * (CSS `gap` only works on flex/grid/multicol containers, not plain block containers). */
const GROUP_GAP_MARGIN_BOTTOM_CLASSNAMES: Record<GroupGap, string> = {
  sm: 'mb-2',
  md: 'mb-4',
  lg: 'mb-6',
};

const GROUP_ALIGN_CLASSNAMES: Record<GroupAlign, string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
};

const GROUP_JUSTIFY_CLASSNAMES: Record<GroupJustify, string> = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
  between: 'justify-between',
};

export function resolveGroupLayoutSettings(block: PageBlock): GroupLayoutSettings {
  return {
    direction: pickStringUnion(
      readString(block.props?.direction),
      GROUP_DIRECTION_VALUES,
      GROUP_DIRECTION_DEFAULT,
    ),
    gap: pickStringUnion(readString(block.props?.gap), GROUP_GAP_VALUES, GROUP_GAP_DEFAULT),
    align: pickStringUnion(readString(block.props?.align), GROUP_ALIGN_VALUES, GROUP_ALIGN_DEFAULT),
    justify: pickStringUnion(
      readString(block.props?.justify),
      GROUP_JUSTIFY_VALUES,
      GROUP_JUSTIFY_DEFAULT,
    ),
    surface: pickStringUnion(
      readString(block.props?.surface),
      GROUP_SURFACE_VALUES,
      GROUP_SURFACE_DEFAULT,
    ),
    backgroundColor: readString(block.props?.backgroundColor),
    backgroundType: readSectionBackgroundType(block.props?.backgroundType),
    backgroundImage: readImageValue(block.props?.backgroundImage),
    backgroundVideo: readVideoValue(block.props?.backgroundVideo),
    backgroundOverlay: parseSectionOverlayOpacity(block.props?.backgroundOverlay),
    backgroundOverlayColor: parseSectionOverlayColor(block.props?.backgroundOverlayColor),
    backgroundFixed: readBoolean(block.props?.backgroundFixed) ?? false,
    backgroundFocusX: readNumber(block.props?.backgroundFocusX) ?? DEFAULT_FOCUS_POINT,
    backgroundFocusY: readNumber(block.props?.backgroundFocusY) ?? DEFAULT_FOCUS_POINT,
    fillWidth: readBoolean(block.props?.fillWidth) ?? false,
  };
}

export function hasGroupMediaBackground(config: GroupLayoutConfig): boolean {
  if (config.backgroundType === 'color') return false;
  return Boolean(config.backgroundImage) || Boolean(config.backgroundVideo);
}

export function hasGroupPaintedBackground(config: GroupLayoutConfig): boolean {
  return hasGroupMediaBackground(config) || Boolean(config.backgroundColor);
}

export const GROUP_MEDIA_SHELL_CLASSNAME = 'relative isolate overflow-hidden rounded-lg';

export function resolveGroupLayout(block: PageBlock, children: PageBlock[]): GroupLayoutConfig {
  const floatedChildren = children.filter(isBlockFloated);
  const floatedDirections = floatedChildren.map(getBlockFloat);
  const hasOpposingFloatDirections =
    floatedDirections.length === 2 &&
    floatedDirections.includes('left') &&
    floatedDirections.includes('right');
  const combinedFloatColumnSpan = floatedChildren.reduce((total, child) => {
    const columnSpan = clampColumnSpan(child.columnSpan);
    const effectiveColumnSpan =
      columnSpan < COLUMN_SPAN_DEFAULT
        ? columnSpan
        : COLUMN_SPAN_DEFAULT * (FLOAT_DEFAULT_MAX_WIDTH_PERCENT / 100);

    return total + effectiveColumnSpan;
  }, 0);

  return {
    ...resolveGroupLayoutSettings(block),
    isFloatLayout: floatedDirections.length > 0,
    hasFittingOpposingFloatPair:
      hasOpposingFloatDirections && combinedFloatColumnSpan <= COLUMN_SPAN_DEFAULT,
  };
}

export function shouldStretchGroupToFillParent(config: GroupLayoutConfig): boolean {
  return !config.isFloatLayout && config.direction === 'vertical' && config.justify === 'between';
}

export function getGroupContainerClassName(
  config: GroupLayoutConfig,
  { blockFlowForAncestorFloat = false }: { blockFlowForAncestorFloat?: boolean } = {},
): string {
  if (blockFlowForAncestorFloat) {
    return 'block w-full';
  }

  if (config.isFloatLayout) {
    // Real CSS float requires this container to be plain block flow, not a flex/grid host —
    // float and clear have no effect on flex or grid items at all (CSS Flexbox/Grid spec).
    // `flow-root` keeps the container's own height correctly wrapping any floated child,
    // without needing a manual clearfix element.
    // `isolate` contains the floated child's z-index inside this group. The floated child must
    // paint above later full-width flow siblings so pointer hit-testing reaches its sortable
    // element instead of activating the sibling (or this group's own drag sensor).
    return 'flow-root isolate w-full';
  }

  const spacing = [
    GROUP_GAP_CLASSNAMES[config.gap],
    GROUP_ALIGN_CLASSNAMES[config.align],
    GROUP_JUSTIFY_CLASSNAMES[config.justify],
  ];

  if (config.direction === 'vertical') {
    return cn('flex flex-col', ...spacing);
  }

  return cn('flex flex-col md:flex-row md:flex-nowrap w-full', ...spacing);
}

/**
 * Keeps a neutral single-child group in the model and DOM, but removes its flex formatting context
 * so an ancestor float can reach either its text or the next neutral group in the chain.
 */
export function shouldUseBlockFlowForAncestorFloat(
  block: PageBlock,
  children: PageBlock[],
  config: GroupLayoutConfig,
  participatesInAncestorFloatFlow: boolean,
): boolean {
  const onlyChild = children[0];
  // A lone child can keep start/center/end placement in block flow (see
  // getGroupChildClassName). Without stretch behavior, cross-axis placement has no extra space to
  // distribute. `between` remains a semantic flex boundary.
  const canRepresentSingleChildLayoutInBlockFlow =
    config.align !== 'stretch' && config.justify !== 'between';

  return (
    participatesInAncestorFloatFlow &&
    block.blockType === BLOCK_TYPE.GROUP &&
    getBlockFloat(block) === null &&
    readString(block.props?.role) === undefined &&
    readBoolean(block.props?.stretch) !== true &&
    !config.isFloatLayout &&
    canRepresentSingleChildLayoutInBlockFlow &&
    config.surface === 'plain' &&
    !hasGroupPaintedBackground(config) &&
    !config.fillWidth &&
    children.length === 1 &&
    (onlyChild?.blockType === BLOCK_TYPE.TEXT || onlyChild?.blockType === BLOCK_TYPE.GROUP)
  );
}

export function getGroupSurfaceClassName(config: GroupLayoutConfig): string | undefined {
  const media = hasGroupMediaBackground(config);
  switch (config.surface) {
    case 'bordered':
      return 'rounded-lg border border-border p-5 md:p-6';
    case 'filled':
      return media
        ? 'rounded-lg p-5 md:p-6'
        : 'rounded-lg bg-card p-5 text-card-foreground md:p-6 [&_.prose]:!text-card-foreground [&_.prose_*]:!text-card-foreground';
    case 'bordered-filled':
      return media
        ? 'rounded-lg border border-border p-5 md:p-6'
        : 'rounded-lg border border-border bg-card p-5 text-card-foreground md:p-6 [&_.prose]:!text-card-foreground [&_.prose_*]:!text-card-foreground';
    case 'muted':
      return media
        ? 'rounded-lg p-5 md:p-6'
        : 'rounded-lg bg-muted p-5 text-muted-foreground md:p-6 [&_.prose]:!text-muted-foreground [&_.prose_*]:!text-muted-foreground';
    case 'feature-band':
      return media
        ? 'relative isolate -mt-16 py-24 md:-mt-24'
        : 'relative isolate -mt-16 py-24 md:-mt-24 before:absolute before:inset-y-0 before:left-1/2 before:-z-10 before:w-screen before:-translate-x-1/2 before:bg-primary [[data-editor-region]_&]:before:w-[100cqi] [&_*]:!text-primary-foreground';
    case 'top-divider':
      return 'border-t border-black/15 pt-8';
    case 'bleed-bottom':
      return '-mb-24 md:-mb-48 relative isolate';
    case 'push-down':
      return 'mt-16 md:mt-32';
    default:
      return undefined;
  }
}

export function getGroupCustomStyle(config: GroupLayoutConfig): CSSProperties | undefined {
  if (hasGroupMediaBackground(config)) return undefined;
  if (!config.backgroundColor) return undefined;
  return { backgroundColor: config.backgroundColor };
}

export function getGroupCustomStyleClassName(config: GroupLayoutConfig): string | undefined {
  if (!hasGroupPaintedBackground(config)) return undefined;
  return 'rounded-lg p-5 md:p-6';
}

function getGroupChildTextAlignClassName(config: GroupLayoutConfig): string | undefined {
  // In horizontal groups, `align` controls cross-axis (vertical) placement, not text direction.
  // Only derive text alignment from `align` in vertical groups, where it controls horizontal placement.
  if (config.direction === 'horizontal') {
    return undefined;
  }
  if (config.align === 'center') {
    return 'text-center';
  }
  if (config.align === 'end') {
    return 'text-right';
  }
  return undefined;
}

const FLOATABLE_BLOCK_TYPES: ReadonlySet<BlockType> = new Set([
  BLOCK_TYPE.IMAGE,
  BLOCK_TYPE.VIDEO,
  BLOCK_TYPE.FORM,
  BLOCK_TYPE.COLLECTION,
  BLOCK_TYPE.GROUP,
  BLOCK_TYPE.EMBED,
]);

/** Block types with supported Wrap controls and float sizing. */
export function canBlockFloat(block: Pick<PageBlock, 'blockType'>): boolean {
  return FLOATABLE_BLOCK_TYPES.has(block.blockType);
}

export function isBlockFloated(block: Pick<PageBlock, 'blockType' | 'props'>): boolean {
  return canBlockFloat(block) && getBlockFloat(block) !== null;
}

const SINGLE_FLOAT_INLINE_GAP_CLASSNAMES: Record<GroupChildFloat, string> = {
  left: 'md:mr-6',
  right: 'md:ml-6',
};

// Keep the gutter inside opposing floats' border-box widths so two half-width floats still fit.
const OPPOSING_FLOAT_INLINE_GAP_CLASSNAMES: Record<GroupChildFloat, string> = {
  left: 'md:pr-3',
  right: 'md:pl-3',
};

/**
 * Floats at `md` and above, using an explicit column span or the legacy-compatible default width.
 * Other children remain in normal block flow so their content wraps around it.
 */
function getFloatChildClassName(block: PageBlock, config: GroupLayoutConfig): string {
  const marginBottomClassName = GROUP_GAP_MARGIN_BOTTOM_CLASSNAMES[config.gap];
  const float = getBlockFloat(block);

  if (float && canBlockFloat(block)) {
    const hasExplicitColumnSpan = clampColumnSpan(block.columnSpan) < COLUMN_SPAN_DEFAULT;
    const maxWidthClassName = hasExplicitColumnSpan
      ? getColumnSpanMaxWidthClassName(block.columnSpan)
      : FLOAT_DEFAULT_MAX_WIDTH_CLASSNAME;
    const inlineGapClassName = config.hasFittingOpposingFloatPair
      ? OPPOSING_FLOAT_INLINE_GAP_CLASSNAMES[float]
      : SINGLE_FLOAT_INLINE_GAP_CLASSNAMES[float];

    return cn(
      // A following flow sibling still owns a full-width block box underneath its wrapped line
      // boxes. Keep the visible float above that box so pointerdown reaches the float's sortable
      // node; otherwise the sibling or ancestor group can become the drag source instead.
      'relative z-10 w-full',
      maxWidthClassName,
      float === 'left' ? 'md:float-left' : 'md:float-right',
      inlineGapClassName,
      marginBottomClassName,
    );
  }

  if (block.blockType === BLOCK_TYPE.FORM || isVisualMediaBlockType(block.blockType)) {
    // Atomic blocks must avoid, rather than occupy a full-width box behind, a sibling float.
    return cn('w-full md:flow-root', marginBottomClassName);
  }

  return cn('w-full', marginBottomClassName);
}

function getBlockFlowInlineAlignmentClassName(config: GroupLayoutConfig): string | undefined {
  const inlinePlacement = config.direction === 'vertical' ? config.align : config.justify;

  if (inlinePlacement === 'center') {
    return config.direction === 'horizontal' ? 'md:mx-auto' : 'mx-auto';
  }
  if (inlinePlacement === 'end') {
    return config.direction === 'horizontal' ? 'md:ml-auto' : 'ml-auto';
  }

  return undefined;
}

export function getGroupChildClassName(
  block: PageBlock,
  config: GroupLayoutConfig,
  { blockFlowForAncestorFloat = false }: { blockFlowForAncestorFloat?: boolean } = {},
): string {
  if (config.isFloatLayout) {
    return getFloatChildClassName(block, config);
  }

  const textAlignClassName =
    block.blockType === 'text' ? getGroupChildTextAlignClassName(config) : undefined;
  const blockFlowAlignmentClassName = blockFlowForAncestorFloat
    ? getBlockFlowInlineAlignmentClassName(config)
    : undefined;

  if (config.direction === 'horizontal') {
    if (shouldForceFullWidthBlock(block)) {
      return cn('min-w-0 w-full');
    }

    if (config.fillWidth) {
      const preservesOwnWidth =
        block.blockType === BLOCK_TYPE.IMAGE && getBlockDisplayWidthStyle(block) !== undefined;
      return cn(
        'min-w-0 w-full',
        preservesOwnWidth ? 'md:flex-initial' : 'md:flex-1 md:w-0',
        textAlignClassName,
      );
    }

    return cn(
      // Default flex (0 1 auto) lets siblings shrink when columnSpan max-widths sum to 100%
      'min-w-0 w-full',
      // Image and group blocks have no intrinsic width, so md:w-auto collapses
      // them; let max-w + w-full give them an explicit rendered width instead.
      // Text is intrinsic-width in flex, but block width:auto fills its container; md:w-fit
      // preserves that width while responsive auto margins preserve center/end justification.
      block.blockType === BLOCK_TYPE.TEXT && blockFlowForAncestorFloat
        ? 'md:w-fit'
        : block.blockType !== BLOCK_TYPE.IMAGE &&
            block.blockType !== BLOCK_TYPE.GROUP &&
            'md:w-auto',
      getColumnSpanMaxWidthClassName(block.columnSpan),
      textAlignClassName,
      blockFlowAlignmentClassName,
    );
  }

  if (isParentBlockType(block.blockType) || shouldForceFullWidthBlock(block)) {
    return cn('min-w-0 w-full', blockFlowAlignmentClassName);
  }

  if (config.align === 'center' || config.align === 'end') {
    return cn(
      'min-w-0 max-w-full',
      // A text child is intrinsic-width inside the original flex container. Once its parent
      // participates in an ancestor's block float flow, width:auto would fill the available
      // block width and make the alignment margin ineffective.
      block.blockType === BLOCK_TYPE.TEXT && blockFlowForAncestorFloat ? 'w-fit' : 'w-auto',
      getColumnSpanMaxWidthClassName(block.columnSpan),
      textAlignClassName,
      blockFlowAlignmentClassName,
    );
  }

  if (block.blockType === 'button') {
    return cn(
      'min-w-0 w-fit max-w-full',
      getColumnSpanMaxWidthClassName(block.columnSpan),
      textAlignClassName,
      blockFlowAlignmentClassName,
    );
  }

  return cn(
    'min-w-0 w-full',
    getColumnSpanMaxWidthClassName(block.columnSpan),
    textAlignClassName,
    blockFlowAlignmentClassName,
  );
}
